import {
  useCallback,
  useEffect,
  useState,
  type ElementType,
  type ReactNode,
} from "react";
import DeleteIcon from "@mui/icons-material/Delete";
import SyncIcon from "@mui/icons-material/Sync";
import {
  Box,
  ButtonBase,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEventListener } from "@wojtekmaj/react-hooks";
import type { Promisable } from "type-fest";

import type {
  CalendarProviderType,
  CalendarSyncConnection,
  GetCalendarSyncConnectionsResponse,
  OAuthCalendarProviderType,
} from "@thalamos/aspire-common";

import { api } from "../../api.js";
import appleLogo from "../../assets/img/apple-logo.svg";
import googleCalendarLogo from "../../assets/img/google-calendar-logo.svg";
import outlookLogo from "../../assets/img/outlook-logo.svg";
import type { OpenWindow } from "./calendarConnect.js";
import { connectInNewTab } from "./calendarConnect.js";
import {
  Banner,
  BannerList,
  Button,
  renderErrorToast,
  renderSuccessToast,
  renderWarningToast,
} from "~/components/design-system/index.js";

export interface CalendarSyncSummary {
  connections: CalendarSyncConnection[];
  enabled: boolean;
}

interface CalendarSyncSectionProps {
  userId: string;
  onSyncStatusChange?: (enabled: boolean) => Promisable<void>;
  onSyncComplete?: () => Promisable<void>;
  /** Renders without Paper wrapper for embedding in a Dialog */
  embedded?: boolean;
  /** Called with summary data for compact inline display */
  onSummaryChange?: (summary: CalendarSyncSummary) => Promisable<void>;
  /** Stand-in for `window.open`, so tests never open a real tab. */
  openWindow?: OpenWindow;
}

export const PROVIDER_LABELS: Record<CalendarProviderType, string> = {
  google: "Google Calendar",
  outlook: "Outlook Calendar",
  ical: "iCal / Apple Calendar",
  stub: "Test Calendar",
};

const PROVIDER_LOGOS: Record<Exclude<CalendarProviderType, "stub">, string> = {
  google: googleCalendarLogo,
  outlook: outlookLogo,
  ical: appleLogo,
};

/** A provider's logo at a square size in pixels; the label beside it names it. */
function ProviderLogo({
  provider,
  size,
}: {
  provider: keyof typeof PROVIDER_LOGOS;
  size: number;
}) {
  return (
    <Box
      component="img"
      src={PROVIDER_LOGOS[provider]}
      alt=""
      sx={{ width: size, height: size, objectFit: "contain" }}
    />
  );
}

const PROVIDER_ICONS: Record<CalendarProviderType, ReactNode> = {
  google: <ProviderLogo provider="google" size={20} />,
  outlook: <ProviderLogo provider="outlook" size={20} />,
  ical: <ProviderLogo provider="ical" size={20} />,
  stub: <SyncIcon fontSize="small" />,
};

function ConnectionStatusChip({
  status,
}: {
  status: CalendarSyncConnection["status"];
}) {
  const colorMap: Record<
    CalendarSyncConnection["status"],
    "success" | "error" | "default"
  > = {
    active: "success",
    error: "error",
    disconnected: "default",
  };

  return (
    <Chip
      size="small"
      label={status}
      color={colorMap[status]}
      variant="outlined"
    />
  );
}

function ProviderTile({
  icon,
  label,
  onClick,
  disabled,
  testId,
}: {
  icon: ReactNode;
  label: string;
  onClick: () => Promisable<void>;
  disabled: boolean;
  testId: string;
}) {
  return (
    <ButtonBase
      onClick={() => {
        void onClick();
      }}
      disabled={disabled}
      data-testid={testId}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 1,
        p: 2,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        "&:hover": { backgroundColor: "action.hover" },
        "&.Mui-disabled": { opacity: 0.5 },
      }}
    >
      {icon}
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {label}
      </Typography>
    </ButtonBase>
  );
}

export function CalendarSyncSection({
  userId,
  onSyncStatusChange,
  onSyncComplete,
  embedded,
  onSummaryChange,
  openWindow,
}: CalendarSyncSectionProps) {
  const [data, setData] = useState<GetCalendarSyncConnectionsResponse | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [icalDialogOpen, setIcalDialogOpen] = useState(false);
  const [icalFeedUrl, setIcalFeedUrl] = useState("");
  const [icalConnecting, setIcalConnecting] = useState(false);

  const applyConnections = useCallback(
    (response: GetCalendarSyncConnectionsResponse) => {
      setData(response);
      void onSyncStatusChange?.(response.calendarSyncEnabled);
      void onSummaryChange?.({
        connections: response.connections,
        enabled: response.calendarSyncEnabled,
      });
    },
    [onSyncStatusChange, onSummaryChange],
  );

  const fetchConnections = useCallback(() => {
    setLoading(true);
    void api.section12
      .getCalendarSyncConnections(userId)
      .then((resp) => {
        applyConnections(resp.data);
      })
      .catch(() => {
        renderErrorToast({ message: "Failed to load calendar connections" });
      })
      .finally(() => {
        setLoading(false);
      });
  }, [userId, applyConnections]);

  useEffect(() => {
    fetchConnections();
  }, [fetchConnections]);

  // A provider's consent page runs in another tab, and that tab closes itself
  // once the connection is made, handing focus back here. Re-reading on focus
  // is how this page learns of it; a change in what is connected means the
  // server has also re-synced the doctor's availability.
  useEventListener(window, "focus", () => {
    void api.section12
      .getCalendarSyncConnections(userId)
      .then((resp) => {
        const known = new Set(data?.connections.map((c) => c.id));
        const changed =
          resp.data.connections.length !== known.size ||
          resp.data.connections.some((c) => !known.has(c.id));
        applyConnections(resp.data);
        if (changed) {
          void onSyncComplete?.();
        }
      })
      .catch(() => {
        // The next focus reads again.
      });
  });

  const handleConnect = async (providerType: OAuthCalendarProviderType) => {
    setConnecting(true);
    try {
      await connectInNewTab(async () => {
        const response = await api.section12.getCalendarSyncAuthUrl(userId, {
          providerType,
        });
        return response.data.authUrl;
      }, openWindow);
    } catch {
      renderErrorToast({ message: "Failed to start calendar connection" });
    } finally {
      setConnecting(false);
    }
  };

  const handleConnectIcal = async () => {
    if (!icalFeedUrl.trim()) return;

    setIcalConnecting(true);
    try {
      const response = await api.section12.connectCalendarFeed(userId, {
        providerType: "ical",
        feedUrl: icalFeedUrl.trim(),
      });
      const connection = response.data;
      if (connection.status === "error") {
        renderWarningToast({
          message:
            "iCal feed connected, but the initial sync failed. It will be retried automatically.",
        });
      } else {
        renderSuccessToast({ message: "iCal feed connected successfully" });
      }
      setIcalDialogOpen(false);
      setIcalFeedUrl("");
      fetchConnections();
      // Backend runs initial sync on connect, so notify parent to refresh
      void onSyncComplete?.();
    } catch {
      renderErrorToast({
        message:
          "Failed to connect iCal feed. Please check the URL and try again.",
      });
    } finally {
      setIcalConnecting(false);
    }
  };

  const handleDisconnect = async (connectionId: string) => {
    try {
      await api.section12.deleteCalendarSyncConnection(userId, connectionId);
      renderSuccessToast({ message: "Calendar disconnected" });
      fetchConnections();
      // Refresh availability after disconnecting (backend resyncs remaining calendars)
      void onSyncComplete?.();
    } catch {
      renderErrorToast({ message: "Failed to disconnect calendar" });
    }
  };

  const handleStopSyncing = async () => {
    try {
      await api.section12.setCalendarSyncEnabled(userId, false);
      // Remove all connections
      if (data?.connections) {
        for (const conn of data.connections) {
          try {
            await api.section12.deleteCalendarSyncConnection(userId, conn.id);
          } catch {
            // best-effort cleanup
          }
        }
      }
      renderSuccessToast({
        message:
          "Calendar sync disabled — you can manage your schedule manually again",
      });
      fetchConnections();
      // Refresh availability after stopping sync (calendar overrides removed)
      void onSyncComplete?.();
    } catch {
      renderErrorToast({ message: "Failed to disable calendar sync" });
    }
  };

  const handleSyncAll = async () => {
    setSyncing(true);
    try {
      await api.section12.triggerCalendarSyncAll(userId);
      renderSuccessToast({ message: "Calendars synced successfully" });
      fetchConnections();
      void onSyncComplete?.();
    } catch {
      renderErrorToast({ message: "Failed to sync calendars" });
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    const loadingContent = (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          p: 3,
        }}
      >
        <CircularProgress size={24} />
      </Box>
    );
    return embedded ? (
      <Box data-testid="section12-calendar-sync-section">{loadingContent}</Box>
    ) : (
      <Paper sx={{ p: 3, mb: 3 }} data-testid="section12-calendar-sync-section">
        {loadingContent}
      </Paper>
    );
  }

  const connections = data?.connections ?? [];

  const Wrapper: ElementType = embedded ? Box : Paper;
  const wrapperProps = embedded
    ? { "data-testid": "section12-calendar-sync-section" }
    : { sx: { p: 3, mb: 3 }, "data-testid": "section12-calendar-sync-section" };

  return (
    <Wrapper {...wrapperProps}>
      <Banner
        bannerType={BannerList.INFO}
        body="Connect calendar to automatically block out busy times."
        dense
      />
      {/* Connected Calendars */}
      {connections.length > 0 && (
        <>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mt: 2,
              mb: 1,
            }}
          >
            <Typography variant="subtitle2">Connected Calendars</Typography>
            <Button
              variant="outlined"
              label={syncing ? "Syncing..." : "Sync Now"}
              customStartIcon={
                syncing ? <CircularProgress size={16} /> : <SyncIcon />
              }
              onClick={() => {
                void handleSyncAll();
              }}
              disabled={syncing}
              small
              testId="calendar-sync-all-btn"
            />
          </Box>
          <Stack spacing={1} sx={{ mb: 2 }}>
            {connections.map((connection) => (
              <Box
                key={connection.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  p: 1.5,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                }}
                data-testid={`calendar-connection-${connection.id}`}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {PROVIDER_ICONS[
                    connection.providerType as CalendarProviderType
                  ] ?? <SyncIcon fontSize="small" color="action" />}
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: "medium",
                      }}
                    >
                      {PROVIDER_LABELS[
                        connection.providerType as CalendarProviderType
                      ] ?? connection.providerType}
                    </Typography>
                    {connection.displayName && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: "text.secondary",
                        }}
                      >
                        {connection.displayName}
                      </Typography>
                    )}
                    {connection.lastSyncedAt && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: "text.secondary",
                          display: "block",
                        }}
                      >
                        Last synced:{" "}
                        {new Date(connection.lastSyncedAt).toLocaleString()}
                      </Typography>
                    )}
                    {connection.lastSyncError && (
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{
                          display: "block",
                        }}
                      >
                        Error: {connection.lastSyncError}
                      </Typography>
                    )}
                  </Box>
                  <ConnectionStatusChip status={connection.status} />
                </Box>
                <Button
                  variant="text"
                  label="Remove"
                  customStartIcon={<DeleteIcon />}
                  onClick={() => {
                    void handleDisconnect(connection.id);
                  }}
                  testId={`calendar-disconnect-btn-${connection.id}`}
                  small
                  error
                />
              </Box>
            ))}
          </Stack>
          <Divider sx={{ my: 2 }} />
        </>
      )}
      {/* Connect New Calendar */}
      <Box
        sx={{
          mt: 2,
          display: "grid",
          gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
          gap: 1.5,
        }}
      >
        <ProviderTile
          icon={<ProviderLogo provider="google" size={40} />}
          label="Google"
          onClick={() => handleConnect("google")}
          disabled={connecting}
          testId="calendar-provider-tile-google"
        />
        <ProviderTile
          icon={<ProviderLogo provider="ical" size={40} />}
          label="Apple"
          onClick={() => {
            setIcalDialogOpen(true);
          }}
          disabled={connecting}
          testId="calendar-provider-tile-apple"
        />
        <ProviderTile
          icon={<ProviderLogo provider="outlook" size={40} />}
          label="Microsoft Outlook"
          onClick={() => handleConnect("outlook")}
          disabled={connecting}
          testId="calendar-provider-tile-outlook"
        />
      </Box>
      {/* Stop syncing option */}
      {connections.length > 0 && (
        <>
          <Divider sx={{ my: 2 }} />
          <Button
            variant="text"
            label="Stop syncing and manage schedule manually"
            onClick={() => {
              void handleStopSyncing();
            }}
            error
            small
            testId="stop-calendar-sync-btn"
          />
        </>
      )}
      {/* iCal Feed URL Dialog */}
      <Dialog
        open={icalDialogOpen}
        onClose={() => {
          setIcalDialogOpen(false);
          setIcalFeedUrl("");
        }}
        maxWidth="sm"
        fullWidth
        data-testid="calendar-feed-dialog"
      >
        <DialogTitle>Connect a calendar feed</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Enter the URL of your calendar&apos;s iCal feed. This is usually a
            webcal:// or https:// link from your calendar app&apos;s sharing
            settings.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            label="Calendar Feed URL"
            placeholder="https://... or webcal://..."
            value={icalFeedUrl}
            onChange={(e) => {
              setIcalFeedUrl(e.target.value);
            }}
            disabled={icalConnecting}
            data-testid="calendar-feed-url-input"
          />
        </DialogContent>
        <DialogActions>
          <Button
            variant="text"
            label="Cancel"
            onClick={() => {
              setIcalDialogOpen(false);
              setIcalFeedUrl("");
            }}
            disabled={icalConnecting}
            testId="calendar-feed-cancel-btn"
          />
          <Button
            variant="contained"
            label={icalConnecting ? "Connecting..." : "Connect"}
            onClick={() => {
              void handleConnectIcal();
            }}
            disabled={icalConnecting || !icalFeedUrl.trim()}
            testId="calendar-feed-connect-btn"
          />
        </DialogActions>
      </Dialog>
    </Wrapper>
  );
}
